/**
 * Module-level Clerk session bridge.
 *
 * `useSession()` is a React hook, but the TanStack `QueryCache.onError` and
 * `MutationCache.onError` handlers in `@lib/query/query-client` are plain
 * functions running outside the React tree. This module is the bridge: the
 * mounted `<ClerkSessionBridge />` component reads the session via
 * `useSession()` and writes it here; the cache handlers read it from here.
 *
 * Concurrent callers that hit a 401 at the same time deduplicate to a
 * single FAPI round-trip via `refreshClerkSession`.
 */
import { CLERK_SESSION_REFRESH_TIMING } from '@lib/auth/constants/clerk-session-refresh-timing';
import { isClerkNoActiveSessionError } from '@lib/clerk/utils/is-clerk-no-active-session-error';
import { isClerkOfflineError } from '@lib/clerk/utils/is-clerk-offline-error';
import type {
  ClerkRefreshOutcomeStatus,
  ClerkRefreshTrigger,
} from '@lib/observability/clerk-refresh-tags/observe-clerk-refresh.client';

export type ClerkSessionLike = {
  getToken: (options?: { skipCache?: boolean }) => Promise<string | null>;
};

export type ClerkRefreshOutcome =
  | { status: 'refreshed' }
  | { status: 'no-session' }
  | { status: 'timed-out' }
  | { status: 'offline' }
  | { status: 'failed'; error: Error };

/**
 * True when callers should continue (invalidate / retry) rather than treat
 * the refresh as a hard auth failure. `timed-out` and `offline` are
 * transient connectivity noise — the session may still be valid — matching
 * the pre-`offline`-status behavior where the caller race resolved to
 * `timed-out` while Clerk retried offline (PORTAL-178).
 *
 * Does **not** include `no-session` (signed out) or `failed` (infra error).
 */
export function isClerkRefreshSessionUsable(
  outcome: ClerkRefreshOutcome
): boolean {
  return (
    outcome.status === 'refreshed' ||
    outcome.status === 'timed-out' ||
    outcome.status === 'offline'
  );
}

/**
 * Optional context callers can pass to `refreshClerkSession` so the
 * observability helper can tag the span with the triggering event source
 * (visibility / pageshow / online / 401 / unknown). First caller wins:
 * the bridge dedupes concurrent callers to a single FAPI round-trip and
 * the first call establishes the trigger context for the cycle.
 */
export interface RefreshClerkSessionOptions {
  trigger?: ClerkRefreshTrigger;
  /** Time the tab spent backgrounded before this refresh, when known. */
  hiddenDurationMs?: number;
  /** First segment of the failing queryKey/mutationKey when triggered by a 401. */
  sourceKey?: string;
}

/**
 * UI-facing state for the "Reconnecting…" indicator. The bridge owns this
 * because it is the single place that knows when a refresh is actually in
 * flight (the inflight dedup is here). Subscribe via
 * {@link addClerkRefreshStateListener} or the React hook
 * `useClerkRefreshIndicator`.
 *
 * - `idle`        → nothing to show
 * - `refreshing`  → a `getToken` round-trip is in flight; the UI hook
 *                   debounces this so fast refreshes (~hundreds of ms) do
 *                   not flicker the banner.
 * - `failed`      → the most recent round-trip ended in `failed` or
 *                   `timed-out`. UI shows a brief failed state; the bridge
 *                   does not auto-clear this — the next successful refresh
 *                   (or a no-session signal that proves we are signed out)
 *                   transitions back to `idle`. The UI hook also clears it
 *                   after a short delay so the banner does not stick around
 *                   forever for a one-off blip.
 */
export type ClerkRefreshIndicatorState = 'idle' | 'refreshing' | 'failed';

let currentSession: ClerkSessionLike | null = null;
let lastRefreshOutcome: ClerkRefreshOutcomeStatus | null = null;
let inFlightRefresh: Promise<ClerkRefreshOutcome> | null = null;
// Per-inflight latch: only one hang log per inflight cycle. Without this,
// N concurrent callers each arm their own caller-bound timer and would
// duplicate Sentry events for the same hung getToken.
let timeoutLoggedForInflight = false;
let hangLogTimer: ReturnType<typeof setTimeout> | undefined;

let currentIndicatorState: ClerkRefreshIndicatorState = 'idle';
const indicatorListeners = new Set<
  (state: ClerkRefreshIndicatorState) => void
>();

function emitIndicatorState(next: ClerkRefreshIndicatorState): void {
  if (next === currentIndicatorState) return;
  currentIndicatorState = next;
  for (const listener of indicatorListeners) {
    try {
      listener(next);
    } catch {
      // Listeners are best-effort UI subscribers; never let one break the
      // refresh path.
    }
  }
}

/**
 * Subscribe to refresh-indicator state changes. Returns an unsubscribe
 * function. Safe to call from React `useSyncExternalStore` — the listener
 * is fired only on state transitions, never on initial subscribe (callers
 * should pair with {@link getClerkRefreshState}).
 */
export function addClerkRefreshStateListener(
  listener: (state: ClerkRefreshIndicatorState) => void
): () => void {
  indicatorListeners.add(listener);
  return () => {
    indicatorListeners.delete(listener);
  };
}

/** Current refresh-indicator state. Used by `useSyncExternalStore`. */
export function getClerkRefreshState(): ClerkRefreshIndicatorState {
  return currentIndicatorState;
}

function getCallerRefreshTimeoutMs(
  options: RefreshClerkSessionOptions
): number {
  const { callerBoundMs, callerBoundAfterHiddenMs, hiddenDurationThresholdMs } =
    CLERK_SESSION_REFRESH_TIMING;
  const hidden = options.hiddenDurationMs;
  if (hidden === undefined) return callerBoundMs;
  // Chrome throttles chained timers after ~5 min hidden; Clerk FAPI can
  // take several seconds to settle on wake (PORTAL-JA: 4.4s after ~30s).
  if (hidden >= hiddenDurationThresholdMs.long) {
    return callerBoundAfterHiddenMs.long;
  }
  if (hidden >= hiddenDurationThresholdMs.medium) {
    return callerBoundAfterHiddenMs.medium;
  }
  return callerBoundMs;
}

/**
 * How long `getToken` may stay pending before we page Sentry. Scales
 * with wake/reconnect context so slow-but-successful recoveries
 * (PORTAL-1AA: 34s after ~68s hidden; ~42s after `online`) do not page
 * the same way PORTAL-JA did at the old caller bound.
 */
function getHangLogTimeoutMs(options: RefreshClerkSessionOptions): number {
  const {
    hangLogMs,
    hangLogAfterHiddenMs,
    hangLogAfterOnlineMs,
    hiddenDurationThresholdMs,
  } = CLERK_SESSION_REFRESH_TIMING;

  if (options.trigger === 'online') {
    return hangLogAfterOnlineMs;
  }

  const hidden = options.hiddenDurationMs;
  if (hidden === undefined) return hangLogMs;
  if (hidden >= hiddenDurationThresholdMs.long) {
    return hangLogAfterHiddenMs.long;
  }
  if (hidden >= hiddenDurationThresholdMs.medium) {
    return hangLogAfterHiddenMs.medium;
  }
  return hangLogMs;
}

export function setClerkSession(session: ClerkSessionLike | null): void {
  currentSession = session;
  // Do not clear `lastRefreshOutcome` here. A null-to-session
  // flicker during Clerk rehydrate on a long-lived /login tab
  // would erase proven `no-session` and let stale authed-query
  // 401s page Sentry on the cooldown path. Signed-out proof
  // clears only when `recordRefreshOutcome` records a terminal
  // outcome (e.g. `refreshed`).
}

function recordRefreshOutcome(outcome: ClerkRefreshOutcomeStatus): void {
  lastRefreshOutcome = outcome;
}

/** Last settled refresh outcome, when refreshClerkSession ran. */
export function getLastClerkRefreshOutcome(): ClerkRefreshOutcomeStatus | null {
  return lastRefreshOutcome;
}

/**
 * True when a settled refresh proved the caller is signed out.
 *
 * A null bridge session alone is not proof — Clerk may still be loading
 * (`session ?? null` during bootstrap / org switch). Only a recorded
 * `no-session` refresh outcome counts.
 */
export function isCallerProvablySignedOut(): boolean {
  return lastRefreshOutcome === 'no-session';
}

/** Clears inflight refresh state between Vitest cases. */
export function resetClerkSessionBridgeStateForTests(): void {
  clearTimeout(hangLogTimer);
  hangLogTimer = undefined;
  inFlightRefresh = null;
  timeoutLoggedForInflight = false;
  lastRefreshOutcome = null;
  emitIndicatorState('idle');
}

export function getClerkSession(): ClerkSessionLike | null {
  return currentSession;
}

async function emitLogError(message: string, error?: Error): Promise<void> {
  if (typeof window === 'undefined') return;
  // Dynamic import required: log.client.ts has `import 'client-only'` and
  // would break any RSC bundle that statically imports this module.
  const { getLogger } = await import('@lib/observability/logging/log.client');
  const logger = getLogger('lib/auth/clerk-session-bridge');
  logger.logError(message, error);
}

type ObservedSettle = (
  outcome: ClerkRefreshOutcomeStatus,
  error?: Error
) => void;

const noopSettle: ObservedSettle = () => undefined;

/**
 * Begin Sentry observation for one in-flight refresh cycle. The helper
 * is `'client-only'` so we dynamic-import it for the same reason
 * `getLogger` is dynamic-imported above. The first call on a fresh page
 * pays one microtask for the import; subsequent calls hit the resolved
 * module cache.
 *
 * `startedAt` must be a `performance.now()` value captured synchronously
 * by the caller immediately before kicking off the refresh — without it,
 * the helper's own `startedAt` is anchored to import-resolution time,
 * which can shift the span / duration by tens or hundreds of
 * milliseconds and even hide real refresh latency entirely when
 * `getToken` resolves before the import finishes loading.
 */
async function beginObservedRefresh(
  options: RefreshClerkSessionOptions,
  startedAt: number
): Promise<ObservedSettle> {
  if (typeof window === 'undefined') return noopSettle;
  try {
    const { observeClerkRefreshAttempt } = await import(
      '@lib/observability/clerk-refresh-tags/observe-clerk-refresh.client'
    );
    const settle = observeClerkRefreshAttempt({
      trigger: options.trigger ?? 'unknown',
      hiddenDurationMs: options.hiddenDurationMs,
      sourceKey: options.sourceKey,
      startedAt,
    });
    return (outcome, error) => settle({ outcome, error });
  } catch {
    return noopSettle;
  }
}

/**
 * Force a fresh Clerk JWT from FAPI (rewriting the `__session` cookie).
 * No-op when there is no active session. Concurrent callers share a
 * single in-flight refresh.
 *
 * The caller's wait is bounded (see `getCallerRefreshTimeoutMs`). The
 * underlying `getToken` keeps running past the bound — the inflight guard
 * still dedupes — but UI paths never block on it. Failures are logged
 * immediately; a caller `timed-out` is logged only if `getToken` is still
 * unsettled after `CLERK_SESSION_REFRESH_TIMING.hangLogMs`. The
 * discriminated outcome lets
 * callers branch instead of guessing.
 *
 * Optional `options` lets the caller tag the Sentry `clerk.refresh` span
 * with the triggering event source (visibility / pageshow / online /
 * 401 / unknown). First caller's options win for the cycle since the
 * bridge dedupes concurrent callers to a single FAPI round-trip.
 */
export function refreshClerkSession(
  options: RefreshClerkSessionOptions = {}
): Promise<ClerkRefreshOutcome> {
  const session = currentSession;
  if (!session) {
    recordRefreshOutcome('no-session');
    return Promise.resolve({ status: 'no-session' });
  }

  if (!inFlightRefresh) {
    timeoutLoggedForInflight = false;
    clearTimeout(hangLogTimer);
    // Indicator follows the actual round-trip, not the per-caller timeout.
    // A `timed-out` caller still leaves the underlying getToken in flight,
    // and the indicator should stay `refreshing` until that resolves — so
    // we transition state from inside the then/catch, not from the caller
    // timeout branch below.
    emitIndicatorState('refreshing');
    // Anchor the observed start time HERE, before the dynamic import in
    // `beginObservedRefresh` can shift the helper's notion of "now". If
    // the import resolves AFTER `getToken` settles, the helper would
    // otherwise stamp the span and its duration against post-import
    // time — making real refreshes look near-instantaneous.
    const refreshStartedAt = performance.now();
    // Span observation runs in parallel with getToken — the dynamic
    // import resolves within a microtask on first call, then is cached.
    // The settle function is awaited inside the then/catch so the span's
    // outcome attribute reflects what getToken actually returned, not
    // what the caller observed via the timeout race.
    const settlePromise = beginObservedRefresh(options, refreshStartedAt);
    hangLogTimer = setTimeout(() => {
      if (!timeoutLoggedForInflight) {
        timeoutLoggedForInflight = true;
        // Log error so we can investigate hung Clerk getToken calls.
        // Cleared in `finally` if getToken settles first — so slow-but-
        // successful wakes never page when hangLogMs is scaled correctly
        // (PORTAL-JA / PORTAL-1AA).
        void emitLogError(
          'Clerk session refresh timed out before getToken settled'
        );
      }
    }, getHangLogTimeoutMs(options));
    inFlightRefresh = (async (): Promise<ClerkRefreshOutcome> => {
      // Skip the FAPI round-trip when the browser already reports offline.
      // Clerk would otherwise retry for ~15s and throw clerk_offline
      // (PORTAL-178); treat as a transient connectivity blip, not a pageable
      // refresh failure.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        emitIndicatorState('failed');
        const settle = await settlePromise;
        settle('offline');
        recordRefreshOutcome('offline');
        return { status: 'offline' };
      }

      try {
        // Start getToken immediately — do not await settlePromise first, or
        // the dynamic-import latency shifts the real refresh start.
        await session.getToken({ skipCache: true });
        emitIndicatorState('idle');
        const settle = await settlePromise;
        settle('refreshed');
        recordRefreshOutcome('refreshed');
        return { status: 'refreshed' };
      } catch (error: unknown) {
        const wrapped =
          error instanceof Error ? error : new Error(String(error));
        const settle = await settlePromise;

        // FAPI authentication_invalid means the session is gone (expired /
        // revoked / signed out elsewhere). The React bridge may still hold a
        // stale session object, so this surfaces as a getToken rejection
        // rather than the early no-session return. Treat it as signed-out —
        // not a refresh infrastructure failure that should page Sentry
        // (PORTAL-K9 / PORTAL-13Q).
        if (isClerkNoActiveSessionError(error)) {
          setClerkSession(null);
          emitIndicatorState('idle');
          settle('no-session');
          recordRefreshOutcome('no-session');
          return { status: 'no-session' };
        }

        // ClerkOfflineError after the offline retry window — connectivity
        // blip, not a bug. Keep the session; show the failed indicator so
        // the reconnecting banner can surface; do not page Sentry
        // (PORTAL-178).
        if (isClerkOfflineError(error)) {
          emitIndicatorState('failed');
          settle('offline');
          recordRefreshOutcome('offline');
          return { status: 'offline' };
        }

        // Log error so we can investigate unexpected Clerk getToken rejections
        void emitLogError('Clerk session refresh failed', wrapped);
        emitIndicatorState('failed');
        settle('failed', wrapped);
        recordRefreshOutcome('failed');
        return { status: 'failed', error: wrapped };
      }
    })().finally(() => {
      clearTimeout(hangLogTimer);
      hangLogTimer = undefined;
      inFlightRefresh = null;
    });
  }

  const callerTimeoutMs = getCallerRefreshTimeoutMs(options);
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<ClerkRefreshOutcome>(resolve => {
    timer = setTimeout(() => {
      resolve({ status: 'timed-out' });
    }, callerTimeoutMs);
  });

  return Promise.race([inFlightRefresh, timeout]).finally(() =>
    clearTimeout(timer)
  );
}
